import { FC, SVGProps, useEffect, useRef, useState, createElement } from 'react';
import styles from './LanguageSwitcher.module.scss';
import appState from '../../state/AppStateContainer';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import CheckIcon from '@material-ui/icons/Check';
import { se, gb, dk, no } from '../../assets/svg/flags';
import { useTranslation } from 'react-i18next';

type langCode = 'sv' | 'en' | 'nb' | 'da';

type lang = {
  id: langCode,
  name: string,
  flag: FC<SVGProps<SVGSVGElement>>
}

type Props = {
  className?: string;
}

const langs: Record<string, lang> = {
  sv: { id: 'sv', name: 'Svenska', flag: se },
  en: { id: 'en', name: 'English', flag: gb },
  nb: { id: 'nb', name: 'Norsk', flag: no },
  da: { id: 'da', name: 'Dansk', flag: dk },
};

const LanguageSwitcher : FC<Props> = ({className = ''}) => {
  const [ showOptions, setShowOptions ] = useState(false);
  const menu = useRef<HTMLDivElement>(null);
  const { i18n } = useTranslation();

  const selectedLangId = appState.state.language;

  const selectLang = (id: langCode) => {
    i18n.changeLanguage(id);
    setShowOptions(false);
  };

  useEffect(() => {
    const handleClickEvent = (e : MouseEvent) => {
      if (menu.current && !menu.current.contains(e.target as Node) && showOptions) {
        setShowOptions(false);
      }
    }
    if (showOptions) {
      document.addEventListener('click', handleClickEvent);
    } else {
      document.removeEventListener('click', handleClickEvent);
    }
    return () => document.removeEventListener('click', handleClickEvent);
  }, [showOptions]);

  const toggleOptions = async () => {
    setShowOptions(!showOptions);
  };

  return (
    <div ref={menu} className={`${styles.container} ${className} ${showOptions ? styles['show-options'] : ''}`}>
      <div className={styles.toggle} onClick={toggleOptions} title={langs[selectedLangId]?.name}>
        { createElement(langs[selectedLangId].flag, { className: styles.flag }) }
        <span className={styles['lang-id']}>{ selectedLangId }</span>
        <span className={styles['toggle-icon']}>
          <ExpandMoreIcon fontSize="small" className={styles.expand}/>
          <ExpandLessIcon fontSize="small" className={styles.contract} />
        </span>
      </div>
      <ul className={styles.options}>
        {Object.values(langs).map((lang) => {
          const isSelected = selectedLangId === lang.id;
          return (
            <li key={lang.id} className={isSelected ? styles.selected : ''} onClick={() => selectLang(lang.id)}>
              { createElement(lang.flag, { className: styles.flag }) }
              { lang.name }
              { isSelected && <CheckIcon className={styles.checkmark} />}
            </li>
          )}
        )}
      </ul>
    </div>
  );
}

export default LanguageSwitcher;
